/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Step 1: Preparing for migration", description = "Scope your migration, plan your data model and tracking strategy, work through the data readiness checklist, and estimate the resources needed to migrate to Hightouch Events.", lastUpdatedDate = new Date(1782345600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Step 1: Preparing for migration",
        description: "Scope your migration, plan your data model and tracking strategy, work through the data readiness checklist, and estimate the resources needed to migrate to Hightouch Events.",
        lastUpdatedDate: "Thu Jun 25 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Determine your migration scope",
          "depth": 0,
          "id": "determine-your-migration-scope"
        }, {
          "value": "Data model and tracking plan",
          "depth": 1,
          "id": "data-model-and-tracking-plan"
        }, {
          "value": "Historical data handling",
          "depth": 1,
          "id": "historical-data-handling"
        }, {
          "value": "Map sources and destinations",
          "depth": 1,
          "id": "map-sources-and-destinations"
        }, {
          "value": "Data readiness checklist",
          "depth": 0,
          "id": "data-readiness-checklist"
        }, {
          "value": "Estimate the time to migrate",
          "depth": 0,
          "id": "estimate-the-time-to-migrate"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      ol: "ol",
      li: "li",
      strong: "strong",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Make the foundational decisions in ", _jsx(_components.a, {
            href: "/events/implementation-plan",
            children: "Plan your Events\nimplementation"
          }), " first — event model, identity,\ncontracts, coverage, and delivery apply to any implementation. This step\ncovers what's specific to a migration: scope, parallel-run setup, and a\nreadiness checklist."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Before you migrate, plan the scope, owners, timeline, and validation approach. Most migrations involve:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Form a migration team and communication plan"
          }), ": Gather a cross-functional team, including data and analytics engineers, analysts, and stakeholders that will carry out the migration and verify data quality. Ensure that you inform affected teams of upcoming changes and provide a channel for concerns or questions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Determine the scope of your migration"
          }), ": Decide whether you're modifying your data model during the migration or keeping it the same, whether you're merging Hightouch Event data with historical data, and whether you want to replace existing event tables or create new ones."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Map downstream destinations"
          }), ": Document any data destinations, dashboards, reports, or other consumers of event data that may need to be updated or validated as part of the migration."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Establish a timeline"
          }), ": Set realistic deadlines for each migration phase based on available personnel."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "determine-your-migration-scope",
        children: "Determine your migration scope"
      }), "\n", _jsx(_components.h3, {
        id: "data-model-and-tracking-plan",
        children: "Data model and tracking plan"
      }), "\n", _jsx(_components.p, {
        children: "Decide on your approach to migration. There are two main options:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Simple migration"
          }), ": Keep your existing data model and event structure as much as possible. This makes validation and updating downstream applications and destinations more straightforward, and may require little or no data wrangling to fit new event data into the existing data model."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Complex migration"
          }), ": Address issues or concerns in your data model during the migration process. Changes during a migration process could also include re-instrumenting events for a new tracking plan or integrating new tooling."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "While these changes can have positive impacts on long-term data quality, each change increases the complexity of the migration and should be evaluated for return on the increased implementation time."
      }), "\n", _jsxs(_components.p, {
        children: ["For most organizations, ", _jsx(_components.strong, {
          children: "start with a simple migration"
        }), ". Minimizing changes increases the likelihood of timely completion and cuts down on the roadblocks that can develop through substantial changes in the data model."]
      }), "\n", _jsx(_components.p, {
        children: "If data quality is a significant issue, a migration to Hightouch Events can be a good occasion to build for long-term data quality and usability."
      }), "\n", _jsx(_components.h3, {
        id: "historical-data-handling",
        children: "Historical data handling"
      }), "\n", _jsx(_components.p, {
        children: "During planning, you should decide where you want the new event data to appear in your warehouse and whether you intend to union the historical and new data."
      }), "\n", _jsxs(_components.p, {
        children: ["Regardless of your choice on unioning, we recommend writing the new Hightouch Event data into a separate schema in your warehouse. This makes it easier to isolate the events while you test and validate your migration, comparing them to historical data. We'll discuss where and how to unify historical and new data in ", _jsx(_components.a, {
          href: "unifying-data",
          children: "Step 5"
        }), " of the guide."]
      }), "\n", _jsx(_components.h3, {
        id: "map-sources-and-destinations",
        children: "Map sources and destinations"
      }), "\n", _jsx(_components.p, {
        children: "During planning, identify:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), ": All the applications you collect events from that you want to migrate to Hightouch Events."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), ": All the places that should receive or consume event data, including your warehouse, BI and analysis tools, and marketing and advertising platforms."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["While most organizations send their events into their warehouse, then reverse ETL (rETL) data to other tools, Hightouch Events also supports ", _jsx(_components.a, {
          href: "/events/event-streaming",
          children: "streaming events"
        }), " directly to other destinations."]
      }), "\n", _jsx(_components.p, {
        children: "It's worthwhile to create a table or other structure to map each source to its destinations and downstream consumers along with the relevant transfer method (rETL or streaming)."
      }), "\n", _jsx(_components.p, {
        children: "Once your team has decided on any changes to the data model and mapped data pipelines from sources to destinations, you can plan for any updates that will be needed for each downstream consumer or tool."
      }), "\n", _jsx(_components.h2, {
        id: "data-readiness-checklist",
        children: "Data readiness checklist"
      }), "\n", _jsx(_components.p, {
        children: "Use this checklist to confirm your data is ready before you start implementing. Each area is a planning input for a later step in this guide—the goal here is to have decisions and owners in place, not to do the work yet."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Area"
            }), _jsx(_components.th, {
              children: "What should be ready"
            }), _jsx(_components.th, {
              children: "Owners"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Migration scope"
            }), _jsxs(_components.td, {
              children: ["A decision on a ", _jsx(_components.a, {
                href: "#data-model-and-tracking-plan",
                children: "simple vs. complex migration"
              }), ", including whether the tracking plan and event model stay the same"]
            }), _jsx(_components.td, {
              children: "Data lead, analytics lead, product owner"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Source inventory"
            }), _jsxs(_components.td, {
              children: ["A complete list of web, mobile, server, and other ", _jsx(_components.a, {
                href: "#map-sources-and-destinations",
                children: "sources"
              }), " to migrate, each with an owner"]
            }), _jsx(_components.td, {
              children: "Engineering, analytics"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Warehouse landing"
            }), _jsxs(_components.td, {
              children: ["A ", _jsx(_components.a, {
                href: "#historical-data-handling",
                children: "separate schema"
              }), " for new Hightouch data during validation, with access for the people who will validate it"]
            }), _jsx(_components.td, {
              children: "Data engineering"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Event schema mapping"
            }), _jsx(_components.td, {
              children: "A field-level map of event names, properties, and types from your current provider to Hightouch"
            }), _jsx(_components.td, {
              children: "Analytics engineering"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Identity plan"
            }), _jsx(_components.td, {
              children: "Agreement on your canonical identifier and anonymous ID behavior."
            }), _jsx(_components.td, {
              children: "Data engineering, product analytics"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Data contracts"
            }), _jsxs(_components.td, {
              children: ["Initial ", _jsx(_components.a, {
                href: "/events/contracts/management",
                children: "contracts"
              }), " for core events, with a planned enforcement level"]
            }), _jsx(_components.td, {
              children: "Engineering, analytics"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Validation plan"
            }), _jsxs(_components.td, {
              children: ["Queries, sample events, a comparison window, and acceptable mismatch reasons. See ", _jsx(_components.a, {
                href: "validating-your-migration",
                children: "Step 3"
              }), "."]
            }), _jsx(_components.td, {
              children: "Analytics, data engineering"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Historical data"
            }), _jsxs(_components.td, {
              children: ["A decision on lookback window, target tables, transformations, and deduplication. See ", _jsx(_components.a, {
                href: "unifying-data",
                children: "Step 4"
              }), "."]
            }), _jsx(_components.td, {
              children: "Data engineering"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Downstream consumers"
            }), _jsxs(_components.td, {
              children: ["A ranked inventory of destinations, dashboards, and reports that depend on event data. See ", _jsx(_components.a, {
                href: "updating-downstream-applications",
                children: "Step 5"
              }), "."]
            }), _jsx(_components.td, {
              children: "Analytics, marketing ops, business owners"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Monitoring and cutover"
            }), _jsxs(_components.td, {
              children: ["Alert thresholds, a cutover date, and a rollback plan. See ", _jsx(_components.a, {
                href: "/events/monitoring",
                children: "Monitor events"
              }), "."]
            }), _jsx(_components.td, {
              children: "Engineering, analytics"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "If you're migrating from a specific platform, the Segment, RudderStack, mParticle, and Amplitude guides note what differs for each of these areas."
      }), "\n", _jsx(_components.p, {
        children: "You're ready to move from planning to implementation when your scope is documented, every source and downstream consumer is inventoried, your landing schema and key event mappings exist, your identity and contract expectations are set, and your validation, historical, and cutover plans are written. At that point, implementation becomes execution instead of discovery."
      }), "\n", _jsx(_components.h2, {
        id: "estimate-the-time-to-migrate",
        children: "Estimate the time to migrate"
      }), "\n", _jsx(_components.p, {
        children: "Once you've broken the migration into the phases on the roadmap, you can estimate effort based on personnel, number of events, and number of sources and destinations."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Our estimates below—and designated personnel—are based on our experience\nhelping customers migrate to Hightouch Events. Every organization is\ndifferent, though, and migration time can vary significantly based on\norganizational and technical factors."
        })
      }), "\n", _jsx(_components.p, {
        children: "For organizations with 10 to 30 event sources, we often see 4 to 6 calendar weeks for a migration. Organizations with 70 to 100 sources can migrate over 8 to 12 weeks."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Planning and preparation"
          }), ": 1 week effort for a ", _jsx(_components.strong, {
            children: "data engineer"
          }), " and ", _jsx(_components.strong, {
            children: "data analyst"
          }), " for research, planning, and documenting the plan, though this scales with changes to the data model and tracking plan."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "SDK implementation"
          }), ": 1 week (or less) for an engineer to implement, test, and deploy new SDK code and initial data contracts."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Configuring destinations"
          }), ": 1 week (or less) for a data analyst to configure, test, and do initial monitoring."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Validating and unioning data"
          }), ": 1 week for a data analyst, though this scales with number and complexity of events and destinations and how you're unioning historical data."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Updating downstream applications and deprecating your prior event collection tool"
          }), ": 1 week or less for an engineer and a data analyst to update downstream data consumers, replace analytics code, re-validate data, and sunset your prior event collection tool."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Remember, these are rough estimates. Your actual timeline may vary based on the complexity of your current setup, the scope of changes you're implementing, and the number of dedicated personnel you have working on the migration."
      }), "\n", _jsxs(_components.p, {
        children: ["Preparing and planning sets up the technical phases that follow. In the next step, you'll ", _jsx(_components.a, {
          href: "implementing-hightouch-events",
          children: "switch SDKs"
        }), " to start collecting data with Hightouch Events."]
      })]
    });
  }
}
export default MDXContent;
